import {
  hasDateOperation,
  hasNonValueOperation,
} from '@plone/volto/components/manage/Blocks/Search/utils';
import config from '@plone/volto/registry';
import cloneDeep from 'lodash/cloneDeep';
import { defineMessages } from 'react-intl';

import AudioFacet from './facets/AudioFacet';
import ContentHubFacet from './facets/ContentHubFacet';
import { getContentHubFacets } from './facets/contentHubFacets';
import EventsFacet from './facets/EventsFacet';
import NewsItemFacet from './facets/NewsItemFacet';
import VideoFacet from './facets/VideoFacet';

const messages = defineMessages({
  searchBlock: {
    id: 'Search block',
    defaultMessage: 'Search block',
  },
  controls: {
    id: 'Controls',
    defaultMessage: 'Controls',
  },
  baseSearchQuery: {
    id: 'Base search query',
    defaultMessage: 'Base search query',
  },
  sectionTitle: {
    id: 'Section title',
    defaultMessage: 'Section title',
  },
  headline: {
    id: 'Headline',
    defaultMessage: 'Headline',
  },
  searchInputPrompt: {
    id: 'Search input label',
    defaultMessage: 'Search input label',
  },
  showSearchInput: {
    id: 'Show search input?',
    defaultMessage: 'Show search input?',
  },
  showSearchButtonTitle: {
    id: 'Show search button?',
    defaultMessage: 'Show search button?',
  },
  showSearchButtonDescription: {
    id: 'The button presence disables the live search, the query is issued when you press ENTER',
    defaultMessage:
      'The button presence disables the live search, the query is issued when you press ENTER',
  },
  searchButtonLabel: {
    id: 'Search button label',
    defaultMessage: 'Search button label',
  },
  searchButtonPlaceholder: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  showSortOn: {
    id: 'Show sorting?',
    defaultMessage: 'Show sorting?',
  },
  sortOnOptions: {
    id: 'Sort on options',
    defaultMessage: 'Sort on options',
  },
  facets: {
    id: 'Facets',
    defaultMessage: 'Facets',
  },
  facet: {
    id: 'Facet',
    defaultMessage: 'Facet',
  },
  label: {
    id: 'Label',
    defaultMessage: 'Label',
  },
  field: {
    id: 'Field',
    defaultMessage: 'Field',
  },
  multipleChoices: {
    id: 'Multiple choices?',
    defaultMessage: 'Multiple choices?',
  },
  hideFacetTitle: {
    id: 'Hide facet?',
    defaultMessage: 'Hide facet?',
  },
  hideFacetDescription: {
    id: 'Hidden facets will still filter the results if proper parameters are passed in URLs',
    defaultMessage:
      'Hidden facets will still filter the results if proper parameters are passed in URLs',
  },
  advancedFacetTitle: {
    id: 'Advanced facet?',
    defaultMessage: 'Advanced facet?',
  },
  advancedFacetDescription: {
    id: 'Advanced facets are initially hidden and displayed on demand',
    defaultMessage:
      'Advanced facets are initially hidden and displayed on demand',
  },
  facetWidget: {
    id: 'Facet widget',
    defaultMessage: 'Facet widget',
  },
  views: {
    id: 'views',
    defaultMessage: 'Views',
  },
  availableViews: {
    id: 'availableViews',
    defaultMessage: 'Available views',
  },
  showTotalResults: {
    id: 'Show total results',
    defaultMessage: 'Show total results',
  },
  filters: {
    id: 'Content Type',
    defaultMessage: 'Content Type',
  },
  news: {
    id: 'News',
    defaultMessage: 'News',
  },
  audio: {
    id: 'Audio',
    defaultMessage: 'Audio',
  },
  custom: {
    id: 'Custom',
    defaultMessage: 'Custom',
  },
  context: {
    id: 'Display only content from the current context.',
    defaultMessage: 'Display only content from the current context.',
  },
  itemsPerPage: {
    id: 'Items per page',
    defaultMessage: 'Items per page',
  },
  publicationDate: {
    id: 'Publication Date',
    defaultMessage: 'Publication Date',
  },
  categoryTitle: {
    id: 'Category',
    defaultMessage: 'Category',
  },
  categoryLabel: {
    id: 'CategoryLabel',
    defaultMessage: 'Category',
  },
  event: {
    id: 'Event',
    defaultMessage: 'Event',
  },
  video: {
    id: 'Video',
    defaultMessage: 'Video',
  },
  contentHub: {
    id: 'Content Hub',
    defaultMessage: 'Content Hub',
  },
});

export const ITEMS_PER_PAGE = 15;

const enhanceSchema = (originalSchema, formData) => {
  const extensionName = 'facetWidgets';
  const extensionType = 'type'; // property name in stored block data
  const variations =
    config.blocks.blocksConfig.search.extensions[extensionName].types;

  const activeItemName = formData?.[extensionType];
  let activeItem = variations?.find((item) => item.id === activeItemName);
  if (!activeItem) activeItem = variations?.find((item) => item.isDefault);

  const schemaEnhancer = activeItem?.['schemaEnhancer'];

  let schema = schemaEnhancer
    ? schemaEnhancer({ schema: cloneDeep(originalSchema), formData })
    : cloneDeep(originalSchema);

  return schema;
};

const FacetSchema = ({ intl }) => ({
  title: intl.formatMessage(messages.facet),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'field', 'type', 'hidden', 'advanced'],
    },
  ],
  properties: {
    title: {
      title: intl.formatMessage(messages.label),
    },
    field: {
      title: intl.formatMessage(messages.field),
      widget: 'select_querystring_field',
      vocabulary: { '@id': 'plone.app.vocabularies.MetadataFields' },
      filterOptions: (options) => {
        // Only allows indexes that provide simple, fixed vocabularies.
        // This should be improved, together with the facets. The querystring
        // widget implementation should serve as inspiration for those dynamic
        // types of facets.
        return Object.assign(
          {},
          ...Object.keys(options).map((k) =>
            Object.keys(options[k].values || {}).length ||
            hasNonValueOperation(options[k].operations) ||
            hasDateOperation(options[k].operations)
              ? { [k]: options[k] }
              : {},
          ),
        );
      },
    },
    multiple: {
      type: 'boolean',
      title: intl.formatMessage(messages.multipleChoices),
      default: false,
    },
    hidden: {
      type: 'boolean',
      title: intl.formatMessage(messages.hideFacetTitle),
      default: false,
      description: intl.formatMessage(messages.hideFacetDescription),
    },
    advanced: {
      type: 'boolean',
      title: intl.formatMessage(messages.advancedFacetTitle),
      default: false,
      description: intl.formatMessage(messages.advancedFacetDescription),
    },
    type: {
      title: intl.formatMessage(messages.facetWidget),
      choices:
        config.blocks.blocksConfig.search.extensions.facetWidgets.types.map(
          ({ id, title }) => [
            id,
            `${intl.formatMessage({ id: id, defaultMessage: title })}`,
          ],
        ),
      defaultValue:
        config.blocks.blocksConfig.search.extensions.facetWidgets.types.find(
          ({ isDefault }) => isDefault,
        ).id,
    },
  },
  required: ['field'],
});

export const getDefaultSearchFilters = (intl) => ({
  news: {
    fullobjects: false,
    query: {
      b_size: ITEMS_PER_PAGE,
      limit: 10000000000,
      query: [
        {
          i: 'portal_type',
          o: 'plone.app.querystring.operation.selection.any',
          v: ['News Item'],
        },
      ],
      sort_order: 'descending',
      sort_on: 'effective',
      sort_order_boolean: true,
    },
    facets: [
      {
        '@id': 'b1eba978-be3b-4d8e-b739-3036b11ef0dd',
        multiple: false,
        hidden: false,
        advanced: false,
        title: intl.formatMessage(messages.publicationDate),
        field: {
          label: intl.formatMessage(messages.publicationDate),
          value: 'effective',
        },
        type: 'daterangeFacet',
      },
      {
        '@id': '965fc4a6-a365-42ad-a3af-08015f51290a',
        multiple: false,
        hidden: false,
        advanced: false,
        title: intl.formatMessage(messages.categoryTitle),
        field: {
          label: intl.formatMessage(messages.categoryLabel),
          value: 'category_id',
        },
        type: 'selectFacet',
      },
    ],
  },
  event: {
    fullobjects: false,
    query: {
      b_size: ITEMS_PER_PAGE,
      limit: 10000000000,
      query: [
        {
          i: 'portal_type',
          o: 'plone.app.querystring.operation.selection.any',
          v: ['Event'],
        },
      ],
      sort_order: 'ascending',
      sort_on: 'start',
      sort_order_boolean: true,
    },
    // facets: [
    //   {
    //     '@id': '71629f88-fcc2-407f-bed9-3cfefac804a5',
    //     advanced: false,
    //     field: {
    //       label: 'Data de início do evento',
    //       value: 'start',
    //     },
    //     hidden: false,
    //     multiple: false,
    //     title: 'Inicia entre:',
    //     type: 'daterangeFacet',
    //   },
    //   {
    //     '@id': '50de02f9-df60-4f8f-8d66-e0322c3f749c',
    //     advanced: false,
    //     field: {
    //       label: 'Data de término do evento',
    //       value: 'end',
    //     },
    //     hidden: false,
    //     multiple: false,
    //     title: 'Termina entre:',
    //     type: 'daterangeFacet',
    //   },
    // ],
  },
  audio: {
    fullobjects: true,
    query: {
      b_size: ITEMS_PER_PAGE,
      limit: 10000000000,
      query: [
        {
          i: 'portal_type',
          o: 'plone.app.querystring.operation.selection.any',
          v: ['Audio'],
        },
      ],
      sort_order: 'descending',
      sort_on: 'effective',
      sort_order_boolean: true,
    },
    facets: [
      {
        '@id': '4cf33cf0-92d7-4dd8-81dc-0a213e7df5a8',
        multiple: false,
        hidden: false,
        advanced: false,
        title: intl.formatMessage(messages.publicationDate),
        field: {
          label: intl.formatMessage(messages.publicationDate),
          value: 'effective',
        },
        type: 'daterangeFacet',
      },
    ],
  },
  video: {
    fullobjects: true,
    query: {
      b_size: ITEMS_PER_PAGE,
      limit: 10000000000,
      query: [
        {
          i: 'portal_type',
          o: 'plone.app.querystring.operation.selection.any',
          v: ['Video'],
        },
      ],
      sort_order: 'descending',
      sort_on: 'effective',
      sort_order_boolean: true,
    },
    facets: [],
  },
  contentHub: {
    fullobjects: true,
    query: {
      b_size: ITEMS_PER_PAGE,
      limit: 10000000000,
      query: [
        {
          i: 'portal_type',
          o: 'plone.app.querystring.operation.selection.any',
          v: ['Image', 'Audio', 'Video'],
        },
      ],
      sort_order: 'descending',
      sort_on: 'effective',
      sort_order_boolean: true,
    },
    facets: getContentHubFacets(intl),
    showSearchButton: true,
  },
});

export const searchViews = {
  news: {
    facet: NewsItemFacet,
  },
  event: {
    facet: EventsFacet,
  },
  audio: {
    facet: AudioFacet,
  },
  video: {
    facet: VideoFacet,
  },
  contentHub: {
    facet: ContentHubFacet,
  },
  // .. Others
};

export const FILTERS_FIELD_NAME = 'filters';

const availableFilterOptions = [
  {
    id: 'news',
    message: messages.news,
  },
  {
    id: 'event',
    message: messages.event,
  },
  {
    id: 'audio',
    message: messages.audio,
  },
  {
    id: 'video',
    message: messages.video,
  },
  {
    id: 'contentHub',
    message: messages.contentHub,
  },
  {
    id: 'custom',
    message: messages.custom,
  },
];

export const CONTEXT_FIELD_NAME = 'context';

export const relativePathQuery = {
  i: 'path',
  o: 'plone.app.querystring.operation.string.relativePath',
  v: '',
};

const SearchSchema = ({ data = {}, intl }) => {
  return {
    title: intl.formatMessage(messages.searchBlock),
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        // fields: ['headline', 'filters'],
        fields: ['filters', 'context', 'itemsPerPage'],
      },
      ...(data?.filters === 'custom'
        ? [
            {
              id: 'searchquery',
              title: intl.formatMessage(messages.baseSearchQuery),
              fields: ['query'],
            },
            {
              id: 'facets',
              title: intl.formatMessage(messages.facets),
              fields: ['facetsTitle', 'facets'],
            },
            {
              id: 'controls',
              title: intl.formatMessage(messages.controls),
              fields: [
                'showSortOn',
                ...(data.showSortOn ? ['sortOnOptions'] : []),
                'showSearchInput',
                ...(data.showSearchInput ?? true ? ['showSearchButton'] : []),
                // ...(data.showSearchInput ? ['searchInputPrompt'] : []),
                // ...(data.showSearchButton ? ['searchButtonLabel'] : []),
                'showTotalResults',
              ],
            },
            {
              id: 'views',
              title: intl.formatMessage(messages.views),
              fields: ['availableViews'],
            },
          ]
        : []),
    ],
    properties: {
      headline: {
        title: intl.formatMessage(messages.headline),
      },
      [CONTEXT_FIELD_NAME]: {
        type: 'boolean',
        title: intl.formatMessage(messages.context),
        default: false,
      },
      itemsPerPage: {
        title: intl.formatMessage(messages.itemsPerPage),
        type: 'number',
        maximum: 30,
        default: 15,
        minimum: 5,
        placeholder: intl.formatMessage(messages.itemsPerPage),
      },
      searchInputPrompt: {
        title: intl.formatMessage(messages.searchInputPrompt),
      },
      showSearchInput: {
        type: 'boolean',
        title: intl.formatMessage(messages.showSearchInput),
        default: true,
      },
      showSearchButton: {
        type: 'boolean',
        title: intl.formatMessage(messages.showSearchButtonTitle),
        description: intl.formatMessage(messages.showSearchButtonDescription),
      },
      showTotalResults: {
        type: 'boolean',
        title: intl.formatMessage(messages.showTotalResults),
        default: true,
      },
      searchButtonLabel: {
        title: intl.formatMessage(messages.searchButtonLabel),
        placeholder: intl.formatMessage(messages.searchButtonPlaceholder),
      },
      showSortOn: {
        type: 'boolean',
        title: intl.formatMessage(messages.showSortOn),
      },
      sortOnOptions: {
        title: intl.formatMessage(messages.sortOnOptions),
        widget: 'array',
      },
      facets: {
        title: intl.formatMessage(messages.facets),
        widget: 'object_list',
        schema: FacetSchema({ intl }),
        schemaExtender: enhanceSchema,
      },
      facetsTitle: {
        title: intl.formatMessage(messages.sectionTitle),
      },
      query: {
        title: 'Query',
      },
      [FILTERS_FIELD_NAME]: {
        title: intl.formatMessage(messages.filters),
        choices: availableFilterOptions.map(({ id, message }) => [
          id,
          intl.formatMessage(message),
        ]),
        default: availableFilterOptions[0].id,
        noValueOption: false,
        // widget: 'array',
      },
    },
    required: ['filters', 'itemsPerPage'],
  };
};

export default SearchSchema;
